import { Table, Badge, Text, Group, ActionIcon } from '@mantine/core'
import { IconExternalLink } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import type { TraceSummary } from '@/types'
import { formatDistanceToNow } from 'date-fns'
import { formatDuration } from '@/utils/format'

interface TraceListProps {
  traces: TraceSummary[]
  loading?: boolean
}

export function TraceList({ traces, loading }: TraceListProps) {
  const navigate = useNavigate()

  const rows = traces.map((trace) => (
    <Table.Tr key={trace.trace_id}>
      <Table.Td>
        <Text size="sm" style={{ fontFamily: 'monospace' }}>
          {trace.trace_id.substring(0, 16)}...
        </Text>
      </Table.Td>
      <Table.Td>
        <Text fw={500}>{trace.root_operation}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{trace.root_service}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{formatDuration(trace.duration_ms)}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{trace.span_count}</Text>
      </Table.Td>
      <Table.Td>
        {trace.error_count > 0 ? (
          <Badge color="red" variant="light">
            {trace.error_count} errors
          </Badge>
        ) : (
          <Badge color="green" variant="light">
            Success
          </Badge>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {formatDistanceToNow(new Date(trace.start_time), { addSuffix: true })}
        </Text>
      </Table.Td>
      <Table.Td>
        <ActionIcon
          variant="subtle"
          onClick={() => navigate(`/traces/${trace.trace_id}`)}
          title="View trace"
        >
          <IconExternalLink size={16} />
        </ActionIcon>
      </Table.Td>
    </Table.Tr>
  ))

  return (
    <Table horizontalSpacing="md" verticalSpacing="sm">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Trace ID</Table.Th>
          <Table.Th>Operation</Table.Th>
          <Table.Th>Service</Table.Th>
          <Table.Th>Duration</Table.Th>
          <Table.Th>Spans</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Started</Table.Th>
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>{rows}</Table.Tbody>
    </Table>
  )
}